import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { specials } from "@/mocks/specials";
import { videos } from "@/mocks/videos";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

const HOT_KEYWORDS = ["独居", "旅行", "海岛", "治愈", "慢生活", "极简"];

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) setKeyword("");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const kw = keyword.trim().toLowerCase();
  const preview = kw
    ? videos.filter((v) =>
        [v.title, ...v.tags].join(" ").toLowerCase().includes(kw),
      ).slice(0, 5)
    : [];

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-24 md:pt-32">
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-2xl animate-popIn rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
            <i className="ri-search-line" />
          </div>
          <input
            autoFocus
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit(keyword);
            }}
            placeholder="搜索视频、标签或关键词…"
            className="w-full bg-transparent text-base text-foreground-950 outline-none placeholder:text-foreground-400 md:text-lg"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
            aria-label="关闭搜索"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-5">
          {!kw && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground-500">
                热门搜索
              </p>
              <div className="flex flex-wrap gap-2">
                {HOT_KEYWORDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => submit(k)}
                    className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          )}

          {kw && (
            <div className="max-h-[46vh] overflow-y-auto">
              {preview.length > 0 ? (
                <ul className="flex flex-col gap-1">
                  {preview.map((v) => (
                    <li key={v.id}>
                      <Link
                        to={`/detail/${v.id}`}
                        onClick={onClose}
                        className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                      >
                        <img
                          src={v.cover}
                          alt={v.title}
                          className="h-12 w-20 shrink-0 rounded-md object-cover object-top"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground-950">
                            {v.title}
                          </p>
                          <p className="truncate text-xs text-foreground-500">
                            {v.author} · {v.duration}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-6 text-center text-sm text-foreground-500">
                  未找到相关视频，试试其他关键词
                </p>
              )}
              <button
                type="button"
                onClick={() => submit(keyword)}
                className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看「{keyword}」的全部结果 <i className="ri-arrow-right-line" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}